import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useRegisterServiceWorker } from "./hooks/useRegisterServiceWorker";
import { Layout } from "./components/Layout";
import Welcome, { hasSeenWelcome } from "./pages/Welcome";
import Login, { Signup } from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import HomeV2 from "./pages/HomeV2";
import Dashboard from "./pages/Dashboard";
import Vehicles from "./pages/Vehicles";
import VehicleNew from "./pages/VehicleNew";
import VehicleDetail from "./pages/VehicleDetail";
import VehicleEdit from "./pages/VehicleEdit";
import VehicleMileage from "./pages/VehicleMileage";
import InterventionNew from "./pages/InterventionNew";
import Alerts from "./pages/Alerts";
import Activity from "./pages/Activity";
import Profile from "./pages/Profile";
import DeleteAccount from "./pages/DeleteAccount";
import DeleteAccountPublic from "./pages/DeleteAccountPublic";
import DeletedVehicles from "./pages/DeletedVehicles";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import LegalMentions from "./pages/LegalMentions";
import Support from "./pages/Support";
import PublicShare from "./pages/PublicShare";
import ScanInvoiceReview from "./pages/ScanInvoiceReview";
import Notifications from "./pages/Notifications";

function LoadingScreen() {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "var(--mf-bg)",
    }} data-testid="loading-screen">
      <div style={{
        width: 32, height: 32, borderRadius: 16,
        border: "3px solid var(--mf-border)", borderTopColor: "var(--mf-brand)",
        animation: "mf-spin 700ms linear infinite",
      }} />
      <style>{`@keyframes mf-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user) {
    // If never seen welcome, send there first
    if (!hasSeenWelcome() && location.pathname !== "/welcome") {
      return <Navigate to="/welcome" replace />;
    }
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  useRegisterServiceWorker();

  return (
    <Routes>
      <Route path="/welcome" element={<PublicOnly><Welcome /></PublicOnly>} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
      <Route path="/share/:token" element={<PublicShare />} />
      <Route path="/delete-account" element={<DeleteAccountPublic />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/legal" element={<LegalMentions />} />
      <Route path="/support" element={<Support />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout><HomeV2 /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicles"
        element={
          <ProtectedRoute>
            <Layout><Vehicles /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicles/new"
        element={
          <ProtectedRoute>
            <Layout><VehicleNew /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicles/:id"
        element={
          <ProtectedRoute>
            <Layout><VehicleDetail /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicles/:id/edit"
        element={
          <ProtectedRoute>
            <Layout><VehicleEdit /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicles/:id/mileage"
        element={
          <ProtectedRoute>
            <Layout><VehicleMileage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/intervention/new"
        element={
          <ProtectedRoute>
            <Layout><InterventionNew /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scan/new"
        element={
          <ProtectedRoute>
            <Layout><ScanInvoiceReview /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Layout><Notifications /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drive"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            <Layout><Alerts /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity"
        element={
          <ProtectedRoute>
            <Layout><Activity /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout><Profile /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/deleted-vehicles"
        element={
          <ProtectedRoute>
            <Layout><DeletedVehicles /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/delete"
        element={
          <ProtectedRoute>
            <Layout><DeleteAccount /></Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}
